import { useState } from 'react';
import { User } from 'firebase/auth';
import type { UserRole, UserAccessLevel } from '@/types';

interface DashboardHeaderProps {
  user: User | null;
  role: UserRole | null;
  accessLevel: UserAccessLevel | null;
  onSignOut: () => void;
}

const DashboardHeader = ({ user, role, accessLevel, onSignOut }: DashboardHeaderProps) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const roleLabel = accessLevel === 'SUPER_ADMIN' ? 'Super Admin' : role || 'No role';

  return (
    <header className="bg-gradient-to-r from-primary-600 to-primary-700 shadow-lg">
      <div className="w-full px-4 py-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <div className="bg-white rounded-lg p-2 shadow-md">
              <img
                src="/logo512.png"
                alt="Sunbeth Global Concepts"
                className="h-10 w-10 object-contain"
              />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">
                Customer Satisfaction Dashboard
              </h1>
              <p className="text-sm text-primary-100 mt-0.5">
                Monitor and analyze customer feedback
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-4 relative">
            <div className="hidden sm:block text-right">
              <p className="text-sm font-medium text-white">
                {user?.displayName || user?.email || 'Unknown User'}
              </p>
              <p className="text-xs text-primary-100 capitalize">{roleLabel}</p>
            </div>
            <div className="relative">
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-primary-600"
              >
                <div className="w-8 h-8 bg-white/30 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                </div>
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
              {showDropdown && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowDropdown(false)}
                  ></div>
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl z-20 border border-gray-200">
                    <div className="px-4 py-3 border-b border-gray-200">
                      <p className="text-sm font-medium text-gray-900">
                        {user?.displayName || 'User'}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                      <span className="inline-block mt-1 px-2 py-0.5 text-xs font-semibold text-primary-600 bg-primary-50 rounded capitalize">
                        {roleLabel}
                      </span>
                    </div>
                    <div className="py-1">
                      <button
                        onClick={onSignOut}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-150 flex items-center space-x-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        <span>Sign Out</span>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default DashboardHeader;

